import {
  CommunicationLogStatus,
  NotificationType,
  Prisma,
  prisma,
  TransactionsAlertFeature,
  TransactionsAlertType,
  TransactionStatus,
  TransactionType,
} from "client";
import { countOrganizationBoothsAndSeatsByUserId } from "services/organazition";
import { calculateUsage, getQuotaLimit } from "@sajilni/utils/canAccessPath";
export const getSassTrack = async ({
  organizationId,
  userId,
}: {
  organizationId?: string;
  userId?: string;
} = {}) => {
  // TODO: revert when approve sass and optimize sass queries
  // return getSassTrackInternal({
  //   organizationId,
  //   userId,
  // });
  return { organizationStats: [] };
};

// TODO: revert when approve sass and optimize sass queries
// TODO: need optimize queries for better performance
const getSassTrackInternal = async ({
  organizationId,
  userId,
}: {
  organizationId?: string;
  userId?: string;
} = {}) => {
  try {
    let transactionsWhere: Prisma.TransactionsWhereInput = {
      OR: [
        {
          endDate: {
            gte: new Date(),
          },
          status: {
            in: [TransactionStatus.ACTIVE],
          },
          type: { in: [TransactionType.SUBSCRIPTION, TransactionType.ADD_ON] },
        },
        {
          type: {
            in: [TransactionType.EVENT_ADD_ON, TransactionType.PER_EVENT],
          },
          status: {
            in: [TransactionStatus.ACTIVE],
          },
        },
      ],
    };
    if (organizationId) {
      transactionsWhere.organizationId = organizationId;
    }
    if (userId) {
      transactionsWhere = {
        ...transactionsWhere,
        organization: { team: { some: { id: userId } } },
      };
    }
    const transactions = await prisma.transactions.findMany({
      where: transactionsWhere,
      select: {
        id: true,
        type: true,
        organizationId: true,
        startDate: true,
        endDate: true,
        cardNumber: true,
        stripeMetadata: true,
        paymentAmount: true,
        currencyShortCode: true,
        organization: {
          select: {
            id: true,
            whiteLabelId: true,
            translations: {
              select: {
                name: true,
              },
            },
            email: true,
            eventIds: true,
            events: {
              select: {
                id: true,
              },
            },
            billingHistory: true,
            PaymentMethodDetails: true,
          },
        },
        qouta: true,
      },
    });

    // Get unique organization IDs with their names and quotas (multiple transactions per org)
    const organizationsMap = new Map<
      string,
      {
        id: string;
        transactionIds: string[];
        email: string;
        whiteLabelId: string;
        name: string;
        quotas: any[];
        eventIds: string[];
        startDate: Date | null;
        endDate: Date | null;
      }
    >();

    transactions.forEach((transaction) => {
      const orgId = transaction.organizationId;
      const existing = organizationsMap.get(orgId);

      if (existing) {
        // Add this transaction's quota and ID to the existing organization
        if (transaction.qouta) {
          existing.quotas.push(transaction.qouta);
        }
        existing.transactionIds.push(transaction.id);

        // Update startDate to earliest and endDate to latest
        if (
          transaction.startDate &&
          (!existing.startDate || transaction.startDate < existing.startDate)
        ) {
          existing.startDate = transaction.startDate;
        }
        if (
          transaction.endDate &&
          (!existing.endDate || transaction.endDate > existing.endDate)
        ) {
          existing.endDate = transaction.endDate;
        }
      } else {
        // Create new entry for this organization
        organizationsMap.set(orgId, {
          id: orgId,
          transactionIds: [transaction.id],
          whiteLabelId: transaction.organization?.whiteLabelId ?? "",
          email: transaction.organization?.email ?? "",
          name: transaction.organization?.translations?.[0]?.name || "Unknown",
          quotas: transaction.qouta ? [transaction.qouta] : [],
          eventIds: transaction.organization?.events?.map((e) => e.id) ?? [],
          startDate: transaction.startDate,
          endDate: transaction.endDate,
        });
      }
    });

    // Get date ranges for current month and year
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    // Count communication logs linked to newsletter/announcement TemplateV2 templates
    const communicationTemplateV2Rows = await prisma.templateV2.findMany({
      where: {
        templateFor: "COMMUNICATION_COMMUNICATION",
        trigger: {
          in: ["ON_ANNOUNCEMENT_SENT", "ON_NEWSLETTER_SENT"],
        },
        type: "EMAIL",
        deletedAt: null,
      },
      select: { id: true },
    });
    const communicationTemplateV2Ids = communicationTemplateV2Rows.map(
      (row) => row.id
    );

    const allEventIds = Array.from(
      new Set(
        Array.from(organizationsMap.values())
          .flatMap((o) => o.eventIds)
          .filter(Boolean)
      )
    );
    const eventsById = new Map<string, { id: string; name: string }>();
    if (allEventIds.length) {
      const events = await prisma.event.findMany({
        where: { id: { in: allEventIds } },
        select: {
          id: true,
          translations: { select: { name: true } },
        },
      });
      events.forEach((e) => {
        eventsById.set(e.id, {
          id: e.id,
          name: e.translations?.[0]?.name || "Unknown",
        });
      });
    }

    // Use Promise.all to fetch counts for all organizations in parallel
    const organizationStats = await Promise.all(
      Array.from(organizationsMap.values()).map(async (org) => {
        const monthlyEmailCount = communicationTemplateV2Ids.length
          ? await prisma.communicationLog.count({
            where: {
              organizationId: org.id,
              status: CommunicationLogStatus.SENT,
              createdAt: {
                gte: startOfMonth,
              },
              templateV2Id: {
                in: communicationTemplateV2Ids,
              },
            },
          })
          : 0;

        // Emails quota stats (MONTHLY only for now) - sum all quotas
        const emailQuotaMax = getQuotaLimit(org.quotas, "maxEmails");
        const emailMonthly = calculateUsage(monthlyEmailCount, emailQuotaMax);

        // Attendees (registrations) stats from profiles across org events,
        // excluding exhibitor/sponsor admins (profiles in EXHIBITORS/SPONSORS participant groups).
        const orgEventIds = org.eventIds ?? [];
        const attendeeBaseWhere: Prisma.ProfileWhereInput = {
          ...(orgEventIds.length ? { eventId: { in: orgEventIds } } : {}),
          OR: [
            {
              orders: { some: {} },
            },
            {
              // TODO: exclude the exhibitors and sponsors admin
              // user: {
              //   userRoleAssignments: {
              //     some: {
              //       role: {
              //         name: {
              //           notIn: ["EXHIBITOR_SUPER_ADMIN", "SPONSOR_SUPER_ADMIN"],
              //         },
              //       },
              //     },
              //   },
              // },
              participantGroups: { some: {} },
            },
          ],
        };

        const [
          attendeesTotal,
          attendeesMonthlyCount,
          attendeesYearlyCount,
          attendeesByEvent,
          { boothsCount, seatsCount },
        ] = orgEventIds.length
            ? await Promise.all([
              prisma.profile.count({ where: attendeeBaseWhere }),
              prisma.profile.count({
                where: {
                  ...attendeeBaseWhere,
                  createdAt: { gte: startOfMonth },
                },
              }),
              prisma.profile.count({
                where: {
                  ...attendeeBaseWhere,
                  createdAt: { gte: startOfYear },
                },
              }),
              prisma.profile.groupBy({
                by: ["eventId"],
                _count: true,
                where: attendeeBaseWhere,
              }),
              // get total number of the seats and booths created
              countOrganizationBoothsAndSeatsByUserId(
                org.id,
                userId ?? undefined
              ),
            ])
            : [0, 0, 0, [] as any[], { boothsCount: 0, seatsCount: 0 }];

        const seatsUsage = calculateUsage(
          seatsCount,
          getQuotaLimit(org.quotas, "seating", "maxSeatsTriggerToForceUpgrade")
        );
        const boothsUsage = calculateUsage(
          boothsCount,
          getQuotaLimit(
            org.quotas,
            "floorPlan",
            "maxBoothsTriggerToForceUpgrade"
          )
        );

        // Calculate attendee limits from all quotas combined
        const attendeesMonthlyQuotaMax = getQuotaLimit(
          org.quotas,
          "maxMonthlyRegistrations"
        );
        const attendeesYearlyQuotaMax = getQuotaLimit(
          org.quotas,
          "maxYearlyRegistrations"
        );
        const attendeesTotalQuotaMax = getQuotaLimit(
          org.quotas,
          "maxAttendees"
        );
        const attendeesSingleEventQuotaMax = getQuotaLimit(
          org.quotas,
          "maxSingleEventThreshold"
        );

        const attendees = {
          total: {
            registered: attendeesTotal,
            ...calculateUsage(attendeesTotal, attendeesTotalQuotaMax),
          },
          monthly: {
            registered: attendeesMonthlyCount,
            ...calculateUsage(attendeesMonthlyCount, attendeesMonthlyQuotaMax),
          },
          yearly: {
            registered: attendeesYearlyCount,
            ...calculateUsage(attendeesYearlyCount, attendeesYearlyQuotaMax),
          },
          events: (attendeesByEvent ?? []).map((row: any) => {
            const eventId = row.eventId as string;
            const count =
              typeof row._count === "number"
                ? row._count
                : row._count?._all ?? 0;
            const eventUsage = calculateUsage(
              count,
              attendeesSingleEventQuotaMax
            );
            return {
              eventId,
              eventName: eventsById.get(eventId)?.name || "Unknown",
              attendees: count,
              registered: count,
              ...eventUsage,
            };
          }),
        };

        const singleEventWorst =
          attendeesSingleEventQuotaMax > 0 && attendees.events?.length
            ? attendees.events.reduce((best: any, cur: any) =>
              (cur?.usagePercentage ?? 0) > (best?.usagePercentage ?? 0)
                ? cur
                : best
            )
            : null;

        const attendeesWithSingleEvent = {
          ...attendees,
          singleEvent: singleEventWorst
            ? {
              eventId: singleEventWorst.eventId,
              eventName: singleEventWorst.eventName,
              registered:
                singleEventWorst.registered ??
                singleEventWorst.attendees ??
                0,
              max: singleEventWorst.max,
              remaining: singleEventWorst.remaining,
              usagePercentage: singleEventWorst.usagePercentage,
              remainingPercentage: singleEventWorst.remainingPercentage,
            }
            : {
              eventId: null,
              eventName: null,
              registered: 0,
              ...calculateUsage(0, attendeesSingleEventQuotaMax),
            },
        };

        return {
          transactions: transactions.filter((t) => t.organizationId === org.id),
          organizationId: org.id,
          organizationName: org.name,
          organizationEmail: org.email,
          organizationWhiteLabelId: org.whiteLabelId,
          transactionIds: org.transactionIds,
          startDate: org.startDate,
          endDate: org.endDate,
          eventsCount: org.eventIds.length,
          emails: {
            monthly: {
              sent: monthlyEmailCount,
              max: emailMonthly.max,
              remaining: emailMonthly.remaining,
              usagePercentage: emailMonthly.usagePercentage,
              remainingPercentage: emailMonthly.remainingPercentage,
            },
          },
          attendees: attendeesWithSingleEvent,
          floorPlan: {
            booths: {
              ...boothsUsage,
            },
            seats: {
              ...seatsUsage,
            },
          },
        };
      })
    );

    return { organizationStats };
  } catch (error) {
    console.error("🚀 ~ getSassTrack ~ Error in getSassTrack:", error);
    return { error };
  }
};

export const getOrganizationTransactions = async (
  organizationId: string,
  userId?: string
) => {
  const transactions = await prisma.transactions.findMany({
    where: {
      organizationId,
      ...(userId ? { organization: { team: { some: { id: userId } } } } : {}),
      status: {
        in: [TransactionStatus.ACTIVE],
      },
      OR: [
        {
          // for subscription transactions
          endDate: {
            gte: new Date(),
          },
        },
        {
          // for add on transactions
          type: {
            in: [TransactionType.ADD_ON, TransactionType.PER_EVENT],
          },
        },
      ],
    },
    include: {
      qouta: true,
    },
  });

  return transactions || [];
};

/**
 * Fetches organization subscriptions for an event by slug.
 * Uses two-step indexed lookup to avoid COLLSCAN:
 * 1. Event by slug (unique index)
 * 2. Organization by eventIds (multikey index)
 * Previously: events: { some: { slug } } caused $lookup + COLLSCAN.
 */
export const getOrganizationTransactionsByEventSlug = async (
  eventSlug: string
) => {
  const event = await prisma.event.findUnique({
    where: { slug: eventSlug },
    select: { id: true },
  });
  if (!event) return [];

  const organization = await prisma.organization.findFirst({
    where: { eventIds: { has: event.id } },
    select: {
      subscriptions: {
        where: {
          status: {
            in: [TransactionStatus.ACTIVE],
          },
          endDate: {
            gte: new Date(),
          },
        },
        include: {
          qouta: true,
        },
      },
    },
  });
  return organization?.subscriptions || [];
};

export const getTransactionsByEventSlug = async (eventSlug: string) => {
  const transactions = await prisma.transactions.findMany({
    where: {
      organization: {
        events: {
          some: { slug: eventSlug },
        },
      },
      endDate: {
        gte: new Date(),
      },
      status: {
        in: [TransactionStatus.ACTIVE],
      },
    },
    include: {
      qouta: true,
      organization: {
        select: {
          id: true,
        },
      },
    },
  });
  return transactions ?? [];
};

export const addTransactionsAlert = async (data: {
  transactionIds?: string[];
  organizationId: string;
  feature: TransactionsAlertFeature;
  type: TransactionsAlertType;
  sentAt?: Date;
  startDate: Date;
  endDate: Date;
}) => {
  const isOrganizationExist = await prisma.organization.findFirst({
    where: { id: data.organizationId },
  });
  if (!isOrganizationExist) {
    throw new Error("Organization not found");
  }

  // Handle both single transactionId and multiple transactionIds
  const transactionIds = data.transactionIds ?? [];

  if (transactionIds.length === 0) {
    throw new Error("At least one transaction ID is required");
  }

  // Verify all transactions exist
  const transactions = await prisma.transactions.findMany({
    where: { id: { in: transactionIds } },
  });

  if (transactions.length !== transactionIds.length) {
    throw new Error("One or more transactions not found");
  }

  // Create the alert with nested junction records
  // Note: Prisma client needs to be regenerated for proper typing
  const transactionsAlert = await prisma.transactionsAlert.create({
    data: {
      organizationId: data.organizationId,
      feature: data.feature,
      type: data.type,
      sentAt: data.sentAt,
      startDate: data.startDate,
      endDate: data.endDate,
      isSent: true,
      transactions: {
        create: transactionIds.map((txId) => ({
          transactionId: txId,
        })),
      },
    },
    include: {
      transactions: true,
    },
  });
  return transactionsAlert;
};

export const getTransactionAlert = async (data: {
  organizationId: string;
  transactionIds?: string[];
  feature: TransactionsAlertFeature;
  type: TransactionsAlertType;
  startDate: Date;
  endDate: Date;
}) => {
  const { transactionIds, startDate, endDate, organizationId, feature, type } =
    data;

  // Build the where clause
  const whereClause: Prisma.TransactionsAlertWhereInput = {
    organizationId,
    feature,
    type,
    startDate: new Date(startDate),
    endDate: new Date(endDate),
    transactions: {
      some: {
        transactionId: {
          in: transactionIds,
        },
      },
    },
  };

  const transactionsAlert = await prisma.transactionsAlert.findFirst({
    where: whereClause,
  });

  return transactionsAlert;
};
